import { useState, useEffect } from 'react';
import { Popover, Radio, Select, Button, Typography, Space, Divider, theme } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { useTranslation } from 'react-i18next';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';

const { Text } = Typography;

interface CategoryFilterPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (filters: FilterValues) => void;
  initialFilters: FilterValues;
  children: React.ReactElement;
}

export interface FilterValues {
  typeIds: number[];
  isActive: boolean | null;
}

const CategoryFilterPopover = ({
  open,
  onOpenChange,
  onApply,
  initialFilters,
  children,
}: CategoryFilterPopoverProps) => {
  const { token } = theme.useToken();
  const { t } = useTranslation('categories');

  const [selectedTypeIds, setSelectedTypeIds] = useState<number[]>(initialFilters.typeIds || []);
  // Status filter: 'active' | 'inactive' | 'all'
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>(
    initialFilters.isActive === true
      ? 'active'
      : initialFilters.isActive === false
        ? 'inactive'
        : 'all'
  );

  // Type options - use existing translations from category.types
  const typeOptions = [
    { label: t('category.types.vendor'), value: 1 },
    { label: t('category.types.customer'), value: 2 },
  ];

  // Reset local state when popover opens with new initial values
  useEffect(() => {
    if (open) {
      setSelectedTypeIds(initialFilters.typeIds || []);
      setStatusFilter(
        initialFilters.isActive === true
          ? 'active'
          : initialFilters.isActive === false
            ? 'inactive'
            : 'all'
      );
    }
  }, [open, initialFilters]);

  const handleResetTypes = () => {
    setSelectedTypeIds([]);
  };

  const handleResetStatus = () => {
    setStatusFilter('all');
  };

  const handleApply = () => {
    // Convert radio status to boolean | null for API
    let isActive: boolean | null = null;
    if (statusFilter === 'active') {
      isActive = true;
    } else if (statusFilter === 'inactive') {
      isActive = false;
    }
    // 'all' means no status filter (null)

    onApply({
      typeIds: selectedTypeIds,
      isActive,
    });
    onOpenChange(false);
  };

  const handleCancel = () => {
    // Reset to initial values
    setSelectedTypeIds(initialFilters.typeIds || []);
    setStatusFilter(
      initialFilters.isActive === true
        ? 'active'
        : initialFilters.isActive === false
          ? 'inactive'
          : 'all'
    );
    onOpenChange(false);
  };

  const content = (
    <div style={{ padding: '12px' }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {/* Type Filter Section */}
        <div
          style={{
            padding: '12px',
            backgroundColor: token.colorFillQuaternary,
            borderRadius: radiusTokens.panel,
          }}
        >
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
            <Text
              strong
              style={{
                fontSize: token.fontSizeSM,
                color: token.colorText,
              }}
            >
              {t('category.filter.type')}
            </Text>
            <Button
              type="link"
              size="small"
              onClick={handleResetTypes}
              style={{ padding: 0, height: 'auto', fontSize: '12px' }}
            >
              {t('category.filter.reset')}
            </Button>
          </div>

          <style>
            {`
              /* Type Select input styling - uses standardized height */
              .type-select-standard .ant-select-selector {
                min-height: ${controlHeightTokens.controlHeight}px !important;
                padding: 4px 8px !important;
                border-radius: ${radiusTokens.card}px !important;
              }

              .type-select-standard .ant-select-selection-item {
                height: auto !important;
                line-height: 20px !important;
                padding: 0px 4px !important;
                font-size: ${token.fontSizeSM}px !important;
                background-color: ${token.colorPrimaryBg} !important;
                border: 1px solid ${token.colorPrimaryBorder} !important;
                border-radius: ${radiusTokens.control}px !important;
                color: ${token.colorPrimary} !important;
              }

              .type-select-standard .ant-select-selection-item-remove {
                color: ${token.colorPrimary} !important;
                font-size: 10px !important;
              }

              /* Dropdown container styling - uses standardized values */
              .type-dropdown-elevated {
                background-color: ${token.colorBgElevated} !important;
                border: 1px solid ${token.colorBorderSecondary} !important;
                border-radius: ${radiusTokens.panel}px !important;
                box-shadow: ${token.boxShadowSecondary} !important;
              }
              .type-dropdown-elevated .ant-select-item {
                padding: 4px 8px !important;
                height: ${controlHeightTokens.controlHeight}px !important;
                display: flex !important;
                align-items: center !important;
              }
              .type-dropdown-elevated .ant-select-item .ant-select-item-option-content {
                height: ${controlHeightTokens.controlHeight}px !important;
                display: flex !important;
                align-items: center !important;
              }
            `}
          </style>
          <Select
            mode="multiple"
            placeholder={t('category.filter.selectTypes')}
            value={selectedTypeIds}
            onChange={setSelectedTypeIds}
            className="type-select-standard"
            classNames={{ popup: { root: 'type-dropdown-elevated' } }}
            listHeight={200}
            style={{
              width: '100%',
            }}
            options={typeOptions}
            allowClear
          />
        </div>

        {/* Status Filter - Radio Button Cards */}
        <div
          style={{
            padding: '12px',
            backgroundColor: token.colorFillQuaternary,
            borderRadius: radiusTokens.panel,
          }}
        >
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
            <Text
              strong
              style={{
                fontSize: token.fontSizeSM,
                color: token.colorText,
              }}
            >
              {t('category.filter.status')}
            </Text>
            <Button
              type="link"
              size="small"
              onClick={handleResetStatus}
              style={{ padding: 0, height: 'auto', fontSize: '12px' }}
            >
              {t('category.filter.reset')}
            </Button>
          </div>
          <style>
            {`
              .status-card-filter {
                flex: 1;
                padding: 10px 12px;
                cursor: pointer;
                transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
                display: flex;
                align-items: center;
                justify-content: center;
              }

              .status-card-filter:hover {
                background-color: ${token.colorPrimaryBg} !important;
                border-color: ${token.colorPrimary} !important;
                transform: translateY(-1px);
              }

              .status-card-filter .ant-radio-wrapper {
                margin-right: 0;
              }
            `}
          </style>
          <Radio.Group
            value={statusFilter}
            onChange={(e: RadioChangeEvent) => setStatusFilter(e.target.value)}
            style={{ width: '100%' }}
          >
            <div className="tw-flex tw-gap-2">
              {(
                [
                  { value: 'active', label: t('category.filter.active') },
                  { value: 'inactive', label: t('category.filter.inactive') },
                  { value: 'all', label: t('category.filter.all') },
                ] as const
              ).map((option) => (
                <div
                  key={option.value}
                  className="status-card-filter"
                  style={{
                    backgroundColor:
                      statusFilter === option.value ? token.colorPrimaryBg : token.colorBgContainer,
                    borderRadius: radiusTokens.card,
                    border: `1.5px solid ${
                      statusFilter === option.value ? token.colorPrimary : token.colorBorder
                    }`,
                  }}
                  onClick={() => setStatusFilter(option.value)}
                >
                  <Radio value={option.value}>
                    <Text strong style={{ fontSize: '13px' }}>
                      {option.label}
                    </Text>
                  </Radio>
                </div>
              ))}
            </div>
          </Radio.Group>
        </div>

        <Divider style={{ margin: 0 }} />

        {/* Action Buttons */}
        <div className="tw-flex tw-gap-2 tw-justify-end">
          <Button onClick={handleCancel} style={{ height: `${controlHeightTokens.controlHeight}px`, fontSize: token.fontSizeSM }}>
            {t('category.filter.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleApply}
            style={{ height: `${controlHeightTokens.controlHeight}px`, fontSize: token.fontSizeSM }}
          >
            {t('category.filter.apply')}
          </Button>
        </div>
      </Space>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={onOpenChange}
      placement="bottomLeft"
      arrow={false}
      overlayStyle={{
        width: '396px',
      }}
      styles={{
        body: {
          padding: 0,
          backgroundColor: token.colorBgElevated,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: radiusTokens.panel,
          boxShadow:
            '0 12px 28px 0 rgba(0, 0, 0, 0.12), 0 6px 12px -6px rgba(0, 0, 0, 0.16), 0 18px 56px 16px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      {children}
    </Popover>
  );
};

export default CategoryFilterPopover;
